import { Briefcase, CheckCircle2, Clock, FileSignature, Hash, Mail, Package } from 'lucide-react';
import { GroupContractMandatItemDto } from 'api/contract';
import { CardsSection } from 'components/layout/Section/CardsSection';
import { InfoItem } from 'components/display/InfoItem';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';

interface MandatLockedSummaryProps {
  mandat: GroupContractMandatItemDto;
}

/**
 * Contenu du panel affiché au clic sur une card de mandat.
 * En-tête style PersonOverview + `CardsSection` + `InfoItem`.
 */
export const MandatLockedSummary = ({ mandat }: MandatLockedSummaryProps) => {
  const { t } = useDynamicTranslation([NsKey.Contract, NsKey.Common]);

  const isSigned = mandat.signatureStatus === 'signed';
  const isPending = mandat.signatureStatus === 'pending';

  const statusLabel = isSigned
    ? t('GROUP_CONTRACT_PROCESS.MANDATS_CARD.STATUS_SIGNED')
    : isPending
    ? t('GROUP_CONTRACT_PROCESS.MANDATS_CARD.STATUS_PENDING')
    : t('GROUP_CONTRACT_PROCESS.MANDATS_CARD.STATUS_DRAFT');

  return (
    <div className="space-y-6">
      {/* En-tête style PersonOverview */}
      <div className="flex items-center gap-4 pb-6 border-b border-primary/20">
        <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
          <FileSignature className="w-8 h-8 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold text-gray-900 truncate">
            {mandat.creditorName ?? t('GROUP_CONTRACT_PROCESS.MANDATS_CARD.CREDITOR_UNKNOWN')}
          </h2>
          <div className="flex flex-wrap gap-4 mt-2 text-sm">
            <span className={`flex items-center gap-1 font-medium ${
              isSigned ? 'text-green-700' : isPending ? 'text-amber-700' : 'text-gray-500'
            }`}>
              {isSigned ? <CheckCircle2 className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
              {statusLabel}
            </span>
          </div>
        </div>
      </div>

      <CardsSection title="Créditeur">
        <div className="grid grid-cols-2 divide-x">
          <div className="pr-6">
            <InfoItem
              icon={<Briefcase />}
              label="Nom du créditeur"
              value={mandat.creditorName}
            />
          </div>
          <div className="pl-6">
            <InfoItem
              icon={<Hash />}
              label="IBAN du créditeur"
              value={mandat.creditorIbanMasked}
            />
          </div>
        </div>
      </CardsSection>

      <CardsSection title={`Produits couverts (${mandat.productNames.length})`}>
        {mandat.productNames.length === 0 ? (
          <p className="text-sm text-gray-500 italic">Aucun produit.</p>
        ) : (
          <div className="grid grid-cols-2 gap-x-6">
            {mandat.productNames.map((name, idx) => (
              <InfoItem
                key={idx}
                icon={<Package />}
                label="Produit"
                value={name}
              />
            ))}
          </div>
        )}
      </CardsSection>

      {isSigned && mandat.rum && (
        <CardsSection title="Référence du mandat">
          <InfoItem
            icon={<Mail />}
            label="RUM"
            value={mandat.rum}
          />
        </CardsSection>
      )}
    </div>
  );
};
